/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Browser-safe repository path utilities
 * 
 * These are pure string manipulation functions that work in the browser.
 * DO NOT import from @shared/utils/repoPaths as it contains Node.js dependencies.
 * 
 * For server-side path resolution, use the backend API endpoints.
 */

/**
 * Normalize a repository URL to a consistent format: owner/repo
 * Examples:
 *   https://github.com/owner/repo.git -> owner/repo
 *   git@github.com:owner/repo.git -> owner/repo
 */
export function normalizeRepoUrl(repoUrl: string): string {
  if (!repoUrl) {
    return '';
  }

  const trimmed = repoUrl.trim().replace(/\.git$/i, '');

  // Match GitHub URLs: github.com/owner/repo or github.com:owner/repo
  const githubMatch = trimmed.match(/github\.com[:/]+([^/]+)\/([^/]+)$/i);
  if (githubMatch) {
    return `${githubMatch[1].toLowerCase()}/${githubMatch[2].toLowerCase()}`;
  }

  // Match SSH format: git@host:owner/repo
  const sshMatch = trimmed.match(/^[^@]+@[^:]+:([^/]+)\/([^/]+)$/);
  if (sshMatch) {
    return `${sshMatch[1].toLowerCase()}/${sshMatch[2].toLowerCase()}`;
  }

  // Generic: strip protocol and take last two path segments
  const strippedProtocol = trimmed.replace(/^https?:\/\//i, '').replace(/^git@/i, '');
  const parts = strippedProtocol.split('/').filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[parts.length - 2].toLowerCase()}/${parts[parts.length - 1].toLowerCase()}`;
  }

  return trimmed.toLowerCase();
}

/**
 * Convert normalized repo URL to a filesystem-safe identifier
 * Example: owner/repo -> owner_repo
 */
export function getRepoIdentifier(repoUrl: string): string {
  const normalized = normalizeRepoUrl(repoUrl);
  return normalized.replace(/[\\/]+/g, '_');
}

/**
 * Get the default display path for a repository (browser-safe, for UI display only)
 * Example: owner/repo -> ~/.velocity/repos/owner_repo
 */
export function getDefaultRepoRelativePath(repoUrl: string): string {
  const identifier = getRepoIdentifier(repoUrl);
  // Browser-safe path joining (just string concatenation)
  // Always use forward slashes for display purposes
  return `~/.velocity/repos/${identifier}`.replace(/\/+/g, '/');
}

/**
 * Constants for display purposes
 */
export const repoPathConstants = {
  DEFAULT_REPOS_ROOT: '~/.velocity/repos',
  DEFAULT_DATA_ROOT: 'data/product',
};
